import { ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Badge } from '~/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';

import type {
  ExportTemplateColumn,
  ExportTemplateDefinition,
  ExportTemplatePreviewRow,
  ExportTransformation,
} from '../types/export.types';
import { appliedTransformations, buildPreviewCells } from '../utils/export-preview-model';

import { PreviewChangesView } from './export-preview/preview-changes-view';
import { PreviewEncodingsView } from './export-preview/preview-encodings-view';
import { PreviewRowDetail } from './export-preview/preview-row-detail';

type CompareTab = 'all' | 'changes' | 'encodings';

interface ExportRowCompareSheetProps {
  /** The clicked product, or null when the panel is closed. */
  row: ExportTemplatePreviewRow | null;
  onClose: () => void;
  header: string[];
  columnsByKey: Record<string, ExportTemplateColumn>;
  transformationsByCode: Record<string, ExportTransformation>;
  template: ExportTemplateDefinition | undefined;
  specUrl: string | null;
  /** 1-based position in the loaded page, for the panel's heading. */
  position: number;
}

/**
 * One product's full comparison, opened by clicking its row.
 *
 * This is the drawer's preview tabs brought to the full-page view, over the
 * same cell model and the same three components — so a value, a diff and an
 * encoding explanation read identically in both places rather than being
 * reimplemented and drifting.
 *
 *   - **All columns** — every column of this product, wrapped and in full.
 *   - **Changes** — only the columns the template rewrote, stored → exported.
 *   - **Encodings** — what each applied encoding does, with worked examples.
 *
 * A side panel rather than an inline expansion: the table is 51 columns wide
 * and a full comparison inline pushed the rows it belonged to off screen. The
 * lead tab follows the data for the same reason the drawer's does — Changes
 * when the template actually rewrote something for this product, All columns
 * when it did not, because an empty diff answers nothing.
 */
export function ExportRowCompareSheet({
  row,
  onClose,
  header,
  columnsByKey,
  transformationsByCode,
  template,
  specUrl,
  position,
}: ExportRowCompareSheetProps) {
  const { t } = useTranslation('exports');
  const [chosenTab, setChosenTab] = useState<CompareTab | null>(null);

  const cells = useMemo(
    () =>
      row
        ? buildPreviewCells({
            preview: { header, rows: [row], rowCount: 1, truncated: false },
            rowIndex: 0,
            template,
            columnsByKey,
            transformationsByCode,
          })
        : [],
    [row, header, template, columnsByKey, transformationsByCode],
  );
  const changedCells = useMemo(() => cells.filter((cell) => cell.changed), [cells]);

  const transformations = useMemo(
    () => appliedTransformations({ template, columnsByKey, transformationsByCode, header }),
    [template, columnsByKey, transformationsByCode, header],
  );

  const changesAvailable = changedCells.length > 0;
  const encodingsAvailable = transformations.length > 0;

  // Resolved, never stored: a tab stranded by switching to a product this
  // template did not rewrite falls back only for as long as it is empty, and is
  // honoured again on the next product that has changes.
  const tabHasContent = (candidate: CompareTab): boolean =>
    candidate === 'all' || (candidate === 'changes' ? changesAvailable : encodingsAvailable);
  const tab: CompareTab =
    chosenTab !== null && tabHasContent(chosenTab)
      ? chosenTab
      : changesAvailable
        ? 'changes'
        : 'all';

  const identifier = row?.encoded[0] ?? '';
  const panelId = 'export-row-compare-panel';

  return (
    <Sheet
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[min(920px,96vw)]"
      >
        <SheetHeader className="space-y-1 border-b px-6 py-4 text-left">
          <SheetTitle className="text-base leading-tight font-semibold">
            {t('browse.compare.title')}
          </SheetTitle>
          <SheetDescription className="text-xs">
            <span className="font-mono break-all">{identifier || '—'}</span>
            <span className="ml-2">{t('browse.compare.position', { position })}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
          {/* A raw template rewrites nothing, so Changes and Formatting rules
              would both be empty — two disabled tabs beside one live one only
              invite the question "why can't I click these". The panel then IS
              the product's columns, with the note below saying why. */}
          {encodingsAvailable && (
            <SegmentedTabs
              value={tab}
              onChange={setChosenTab}
              panelId={panelId}
              ariaLabel={t('browse.compare.tabsAria')}
              className="w-auto sm:max-w-none"
              options={[
                {
                  value: 'all',
                  label: t('browse.compare.all'),
                  tabId: 'export-compare-tab-all',
                  ...(cells.length > 0 ? { badge: String(cells.length) } : {}),
                },
                {
                  value: 'changes',
                  label: t('preview.tabs.changes'),
                  tabId: 'export-compare-tab-changes',
                  ...(changedCells.length > 0 ? { badge: String(changedCells.length) } : {}),
                  // Disabled rather than hidden: the template DOES rewrite
                  // columns (the tabs only render when it does), just none of
                  // this product's — and a tab that appears and vanishes per
                  // row is worse to use than one that says why it is empty.
                  disabled: !changesAvailable,
                  tooltip: t('preview.tabs.noChangesYet'),
                },
                {
                  value: 'encodings',
                  label: t('preview.tabs.encodings'),
                  tabId: 'export-compare-tab-encodings',
                  ...(transformations.length > 0 ? { badge: String(transformations.length) } : {}),
                },
              ]}
            />
          )}

          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={`export-compare-tab-${tab}`}
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {tab === 'all' && (
              <div className="rounded-md border">
                <PreviewRowDetail cells={cells} />
              </div>
            )}
            {tab === 'changes' && <PreviewChangesView cells={changedCells} />}
            {tab === 'encodings' && (
              <PreviewEncodingsView transformations={transformations} specUrl={specUrl} />
            )}
          </div>

          {tab === 'changes' && (
            <p className="text-muted-foreground shrink-0 text-xs">
              <ArrowRight aria-hidden="true" className="mr-1 inline size-3" />
              {t('browse.compare.changesNote', { count: changedCells.length })}
            </p>
          )}
          {!changesAvailable && tab === 'all' && (
            <p className="text-muted-foreground shrink-0 text-xs">
              <Badge variant="outline" className="pointer-events-none mr-1.5 text-[10px]">
                {t('browse.compare.unchanged')}
              </Badge>
              {t('preview.noChangeSummary')}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
